import { Component, EventEmitter, Input, OnChanges, Output, signal } from '@angular/core';
import {
  DeviceConfigurationDtoIncoming,
  DeviceConfigurationDtoOutbound,
  DisplayPortFormItem,
  DisplayPortRequest,
  UpdateDeviceConfigurationEvent,
} from '../models/device-configuration.model';
import { FormsModule } from '@angular/forms';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Toggle } from '../../../shared/components/forms/toggle';

export type DialogMode = 'add' | 'edit' | 'view';

@Component({
  selector: 'device-configuration-view',
  imports: [FormsModule, Modal, ModalFooterDirective, FormField, TextInput, NumberInput, Toggle],
  templateUrl: './device-configuration-view.component.html',
})
export class DeviceConfigurationDialogComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Input() mode: DialogMode = 'add';
  @Input() viewOrEditDeviceConfigurationDto: DeviceConfigurationDtoIncoming | null = null;
  @Output() configurationSaved = new EventEmitter<DeviceConfigurationDtoOutbound>();
  @Output() configurationUpdated = new EventEmitter<UpdateDeviceConfigurationEvent>();
  @Output() dialogClosed = new EventEmitter<void>();

  /** Drives the modal shell's two-way `open`; closing routes through closeDialog(). */
  readonly open = signal(true);

  name = '';
  timezone = '';
  timeSyncUrl = '';
  displayWidth = 0;
  displayHeight = 0;
  ports: DisplayPortFormItem[] = [];
  customProperties: { key: string; value: string }[] = [];
  deletedPortIds: number[] = [];

  get isViewMode(): boolean { return this.mode === 'view'; }
  get isEditMode(): boolean { return this.mode === 'edit'; }
  get isAddMode(): boolean { return this.mode === 'add'; }

  get modalTitle(): string {
    return this.isAddMode
      ? 'Add Device Configuration'
      : this.isEditMode
        ? 'Edit Device Configuration'
        : 'Device Configuration';
  }

  ngOnChanges(): void {
    if (this.isOpen) {
      this.open.set(true);
      this.deletedPortIds = [];
      if (this.mode === 'add') {
        this.resetToBlank();
      } else if (this.viewOrEditDeviceConfigurationDto) {
        this.loadFromIncoming(this.viewOrEditDeviceConfigurationDto);
      }
    }
  }

  switchToEdit(): void {
    this.mode = 'edit';
  }

  private resetToBlank(): void {
    this.name = '';
    this.timezone = '';
    this.timeSyncUrl = '';
    this.displayWidth = 0;
    this.displayHeight = 0;
    this.ports = [];
    this.customProperties = [];
  }

  private loadFromIncoming(conf: DeviceConfigurationDtoIncoming): void {
    this.name = conf.name;
    this.timezone = conf.timezone;
    this.timeSyncUrl = conf.timeSyncUrl;
    this.displayWidth = conf.displayWidth;
    this.displayHeight = conf.displayHeight;
    this.ports = conf.displayPorts.map(p => ({
      id: p.id,
      portName: p.portName,
      rowNumber: p.rowNumber,
      columnNumber: p.columnNumber,
      reverseOrientation: p.reverseOrientation,
      width: p.width,
      height: p.height,
      vcom: p.vcom,
      waveform: p.waveform,
      epdFrequency: p.epdFrequency,
    }));
    this.customProperties = Object.entries(conf.properties ?? {}).map(([key, value]) => ({ key, value }));
  }

  trackByIndex(index: number, _item: unknown): number {
    return index;
  }

  addDisplayPort(): void {
    this.ports.push({
      portName: '',
      rowNumber: 0,
      columnNumber: 0,
      reverseOrientation: false,
      width: 0,
      height: 0,
      vcom: 0,
      waveform: '',
      epdFrequency: 0,
    });
  }

  removeDisplayPort(index: number): void {
    const port = this.ports[index];
    if (port.id !== undefined) {
      this.deletedPortIds.push(port.id);
    }
    this.ports.splice(index, 1);
  }

  addCustomProperty(): void {
    this.customProperties.push({ key: '', value: '' });
  }

  removeCustomProperty(index: number): void {
    this.customProperties.splice(index, 1);
  }

  private buildProperties(): Record<string, string> {
    return this.customProperties.reduce((acc, p) => {
      acc[p.key] = p.value;
      return acc;
    }, {} as Record<string, string>);
  }

  private toPortRequest(p: DisplayPortFormItem): DisplayPortRequest {
    return {
      portName: p.portName,
      rowNumber: p.rowNumber,
      columnNumber: p.columnNumber,
      reverseOrientation: p.reverseOrientation,
      width: p.width,
      height: p.height,
      vcom: p.vcom,
      waveform: p.waveform,
      epdFrequency: p.epdFrequency,
    };
  }

  /** Modal primary button: switches to edit in view mode, otherwise saves. */
  onPrimary(): void {
    if (this.isViewMode) {
      this.switchToEdit();
      return;
    }
    this.saveConfiguration();
  }

  saveConfiguration(): void {
    if (this.isAddMode) {
      this.configurationSaved.emit({
        name: this.name,
        timezone: this.timezone,
        timeSyncUrl: this.timeSyncUrl,
        displayWidth: this.displayWidth,
        displayHeight: this.displayHeight,
        properties: this.buildProperties(),
        displayPorts: this.ports.map(p => this.toPortRequest(p)),
      });
    } else {
      this.configurationUpdated.emit({
        configId: this.viewOrEditDeviceConfigurationDto!.id,
        parentFields: {
          name: this.name,
          timezone: this.timezone,
          timeSyncUrl: this.timeSyncUrl,
          displayWidth: this.displayWidth,
          displayHeight: this.displayHeight,
          properties: this.buildProperties(),
        },
        newPorts: this.ports.filter(p => p.id === undefined).map(p => this.toPortRequest(p)),
        updatedPorts: this.ports
          .filter(p => p.id !== undefined)
          .map(p => ({ id: p.id!, data: this.toPortRequest(p) })),
        deletedPortIds: this.deletedPortIds,
      });
    }
    this.closeDialog();
  }

  closeDialog(): void {
    this.dialogClosed.emit();
  }
}
